import axios from "axios";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Add } from "@mui/icons-material";
import { Box, Chip, CircularProgress, MenuItem, Select, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Layout from "Components/Layout/Layout";
import PaperTable from "Components/PaperTable";
import TextBlock from "Components/Layout/TextBlock";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarButton from "Components/Sidebar/SidebarButton";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import FetchNextButton from "Components/FetchNextButton";
import dateFormats from "system/dateFormats";
import moment from "moment";
import useQuery, { IQuery } from "system/useQuery";
import useAsyncEffect from "system/useAsyncEffect";
import VehiclesNavigation from "Navigation/VehiclesNavigation";
import { CollectionSession, CollectionSessionPerspective } from "system/Domain";

export interface CollectionSessionsOverviewQuery extends IQuery {
	perspective: CollectionSessionPerspective;
}

const statusLabels: { [k: string]: { label: string, color: string } } = {
	Created: { label: "Offen", color: "warning" },
	Completed: { label: "Abgeschlossen", color: "success" },
	Cancelled: { label: "Abgebrochen", color: "default" }
};

const CollectionSessionsOverview = () => {
	const history = useHistory();
	const [sessions, setSessions] = useState<CollectionSession[]>();
	const [isFetching, setIsFetching] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	const [query, setQuery, fetchNext] = useQuery<CollectionSessionsOverviewQuery>("collectionSessionsOverviewQuery", {
		perspective: "Created",
		skip: 0,
		take: 20
	});

	useAsyncEffect(async (cancelToken) => {
		setIsFetching(true);

		try {
			const { data } = await axios.get<CollectionSession[]>(`/api/sales/collection-sessions`, {
				params: query,
				cancelToken: cancelToken.getAxiosToken()
			});

			setSessions(s => query.skip > 0 ? [...(s ?? []), ...data] : data);
		} finally {
			setIsFetching(false);
		}
	}, [query]);

	const createSession = async () => {
		setIsCreating(true);

		try {
			const { data } = await axios.post<CollectionSession>(`/api/sales/collection-sessions`, {});

			history.push(`/vehicles/collection-sessions/${data.id}`);
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<Layout
			title="Sammelsitzungen Ausgangsprotokolle"
			navigation={<VehiclesNavigation />}
			sidebar={
				<Sidebar>
					<SidebarGroup>
						<SidebarButton
							startIcon={<Add />}
							color="primary"
							label="Neue Sammelsitzung"
							disabled={isCreating}
							onClick={createSession}
						/>
					</SidebarGroup>
					<SidebarGroup>
						<Select
							fullWidth
							variant="standard"
							value={query.perspective}
							onChange={(e) => setQuery(q => ({ ...q, perspective: e.target.value as CollectionSessionPerspective, skip: 0 }))}
						>
							<MenuItem value="Created">Offen</MenuItem>
							<MenuItem value="Completed">Abgeschlossen</MenuItem>
							<MenuItem value="Cancelled">Abgebrochen</MenuItem>
							<MenuItem value="All">Alle</MenuItem>
						</Select>
					</SidebarGroup>
				</Sidebar>
			}
		>
			{!sessions && (
				<Box m={2}>
					<CircularProgress size={48} />
				</Box>
			)}
			{sessions && (
				<>
					{sessions.length === 0 && (
						<Box>Keine Sammelsitzungen vorhanden</Box>
					)}
					{sessions.length > 0 && (
						<PaperTable>
							<TableHead>
								<TableRow>
									<TableCell>Erstellt</TableCell>
									<TableCell>Fahrzeuge</TableCell>
									<TableCell>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sessions.map(s => (
									<TableRow
										key={s.id}
										hover
										onClick={() => history.push(`/vehicles/collection-sessions/${s.id}`)}
									>
										<TableCell>
											<TextBlock
												primary={moment(s.dateCreated).format(dateFormats.dateTime)}
												secondary={s.createdBy?.name}
											/>
										</TableCell>
										<TableCell>{s.members.filter(m => !m.dateRemoved).length}</TableCell>
										<TableCell>
											<Chip
												size="small"
												label={statusLabels[s.status]?.label ?? s.status}
												color={statusLabels[s.status]?.color as any}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</PaperTable>
					)}
					<FetchNextButton
						hidden={!sessions || sessions.length < query.take + query.skip}
						mb={3}
						onNext={fetchNext}
						disabled={isFetching}
					/>
				</>
			)}
		</Layout>
	);
};

export default CollectionSessionsOverview;
